import { useEffect, useState, useCallback } from "react";
import { Users, UserPlus, Trash2, Loader2, Copy, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePlan } from "@/context/PlanContext";
import LockedFeature from "@/components/LockedFeature";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  active: "bg-emerald-500/15 text-emerald-300",
  invited: "bg-amber-500/15 text-amber-300",
  suspended: "bg-red-500/15 text-red-300",
};

export default function Team() {
  const { hasFeature, loading: planLoading } = usePlan();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "member" });
  const [inviting, setInviting] = useState(false);

  const load = useCallback(() => {
    api.get("/team").then((r) => setData(r.data)).catch(() => setData({ members: [], seat_limit: 0, seats_used: 0, can_manage: false }));
    api.get("/team/roles").then((r) => setRoles(r.data.roles)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (planLoading || !data) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;
  }
  if (!hasFeature("team_accounts")) {
    return <LockedFeature title="Team Accounts" featureKey="team_accounts" requiredPlan="enterprise"
      description="Invite team members, assign role-based permissions and collaborate across your business — available on the Enterprise plan." />;
  }

  const invite = async () => {
    if (!form.email) return toast.error("Enter an email address");
    setInviting(true);
    try {
      const r = await api.post("/team/invite", form);
      toast.success(`Invitation sent to ${form.email}`);
      if (r.data.invite_url) {
        navigator.clipboard?.writeText(r.data.invite_url).catch(() => {});
      }
      setForm({ email: "", name: "", role: "member" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInviting(false); }
  };

  const changeRole = async (id, role) => {
    try { await api.put(`/team/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSuspend = async (m) => {
    const status = m.status === "suspended" ? "active" : "suspended";
    try { await api.put(`/team/${m.member_id}`, { status }); toast.success(`Member ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this team member? Their access is revoked; their data is preserved.")) return;
    try { await api.delete(`/team/${id}`); toast.success("Team member removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const seatsFull = data.seat_limit != null && data.seats_used >= data.seat_limit;

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="team-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl fv-gold-gradient flex items-center justify-center">
          <Users className="w-5 h-5 text-[#0A0A0B]" />
        </div>
        <div>
          <h1 className="text-2xl font-heading text-white">Team & Collaboration</h1>
          <p className="text-sm text-[#A1A1AA]">
            {data.seats_used} of {data.seat_limit ?? "∞"} seats used
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-6" data-testid="team-invite-card">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#D4AF37]" /> Invite a team member</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email address" data-testid="team-invite-email" className="bg-[#0d0d0f] border-white/10 text-white md:col-span-2" />
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name (optional)" data-testid="team-invite-name" className="bg-[#0d0d0f] border-white/10 text-white" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="team-invite-role" className="bg-[#0d0d0f] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={invite} disabled={inviting || seatsFull} data-testid="team-invite-btn"
          className="mt-4 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
          {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Copy className="w-4 h-4 mr-2" /> Send invite (link copied)</>}
        </Button>
        {seatsFull && <p className="text-xs text-amber-400 mt-3">All seats used — upgrade or remove a member to invite more.</p>}
      </div>

      <div className="space-y-2" data-testid="team-members-list">
        <h3 className="text-white font-medium mb-2">Members</h3>
        {data.members.length === 0 && <p className="text-sm text-[#71717A]">No team members yet. Invite your first teammate above.</p>}
        {data.members.map((m) => (
          <div key={m.member_id} data-testid={`team-member-${m.member_id}`}
            className="rounded-xl border border-white/8 bg-[#121214] px-5 py-3.5 flex items-center gap-4">
            <div className="w-9 h-9 rounded-full fv-gold-gradient flex items-center justify-center text-[#0A0A0B] font-semibold text-sm shrink-0">
              {(m.name || m.email)[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm truncate">{m.name || m.email}</div>
              <div className="text-xs text-[#71717A] truncate">{m.email}</div>
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[m.status] || "bg-white/10 text-[#A1A1AA]"}`}>{m.status}</span>
            <Select value={m.role} onValueChange={(v) => changeRole(m.member_id, v)}>
              <SelectTrigger data-testid={`team-role-${m.member_id}`} className="w-32 h-8 bg-[#0d0d0f] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            {m.status !== "invited" && (
              <button onClick={() => toggleSuspend(m)} data-testid={`team-suspend-${m.member_id}`}
                title={m.status === "suspended" ? "Reactivate" : "Suspend"}
                className="text-[#A1A1AA] hover:text-white transition-colors"><ShieldCheck className="w-4 h-4" /></button>
            )}
            <button onClick={() => remove(m.member_id)} data-testid={`team-remove-${m.member_id}`}
              className="text-red-400/70 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
